import {
  all,
  spawn,
  call,
  put,
  takeLatest,
  takeEvery,
  delay,
} from 'redux-saga/effects';

import request from 'utils/request';
import {
  SIGNUP_SOURCE,
  UPDATE_TNC_URL,
  REDEEM_VOUCHER_URL,
  VALIDATE_TOKEN_URL,
  TNC,
} from 'utils/constants';
import {
  getUTMObject,
  getItemFromLocalStorage,
  setItemsInLocalStorage,
  useQuery,
  getReferrerFromURL,
  getDeviceType,
} from 'utils/common';
import { GET_TNC_URL } from 'constants/url';
import { EMAIL_NOT_VERIFIED } from 'containers/LandingPage/constants';

import { showToast, hideToast } from '../Toast/actions';
import {
  disableLoaderAction,
  saveUserDetails,
  updateFormStatus,
  saveMerchantDetails,
  googleLoginAction,
  validateTokenSuccess,
  toggleOTPModalAction,
  setSignupFirstStepDataAction,
} from './actions';

import {
  CREATE_MERCHANT,
  CREATE_MERCHANT_URL,
  LOGIN,
  SIGNUP,
  USER_DETAILS_COMPLETED,
  UPDATE_MERCHANT_DETAILS,
  UPDATE_MERCHANT_DETAILS_URL,
  CREATE_FRESHSALES_LEAD,
  LEAD_SOURCE_ID_MAPPING,
  AUTHENTICATE_EMAIL_USER,
  AUTHENTICATE_EMAIL_USER_URL,
  AUTHENTICATE_ALIAS,
  AUTHENTICATE_ALIAS_URL,
  GOOGLE_LOGIN,
  GOOGLE_LOGIN_URL,
  GOOGLE_SIGNUP,
  GOOGLE_SIGNUP_URL,
  REDEEM_VOUCHER,
  VERIFY_LOGIN_OTP,
  VERIFY_LOGIN_OTP_URL,
  RESEND_LOGIN_OTP,
  RESEND_LOGIN_OTP_URL,
  loginSignupErrorCode,
  CHECK_EMAIL_EXISTS,
  CHECK_EMAIL_EXISTS_URL,
  SIGNUP_MERCHANT_URL,
  SIGNUP_MERCHANT,
  SEND_SIGNUP_OTP,
  SEND_SIGNUP_OTP_URL,
  VERIFY_IMPORTS,
  VERIFY_IMPORTS_URL,
  VERIFY_EMAIL_OTP_URL,
  VERIFY_EMAIL_OTP,
  RESEND_EMAIL_OTP,
  RESEND_EMAIL_OTP_URL,
  DEFAULT_STATUS,
} from './constants';

import { basename } from '../../store';

export function getMetaData() {
  const metadata = getUTMObject();

  // eslint-disable-next-line react-hooks/rules-of-hooks
  let sourceAction = useQuery().get('source-action');
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const partnerRefCode = useQuery().get('refCode');

  sourceAction = sourceAction ? decodeURIComponent(sourceAction) : 'Web';

  metadata.source = SIGNUP_SOURCE;
  metadata.cfLeadSource = partnerRefCode
    ? 'partner-submerchant-referrallink'
    : sourceAction;

  return metadata;
}

export function* createMerchant({ data }) {
  try {
    const { url, method } = CREATE_MERCHANT_URL;
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const partnerRefCode = useQuery().get('refCode');
    const options = {
      method,
      body: JSON.stringify({
        ...data,
        refCode: data.refCode || partnerRefCode || '',
        metadata: getMetaData(),
      }),
      track: true,
    };

    const response = yield call(request, url, options);

    setItemsInLocalStorage({ deviceId: data.deviceId });
    yield put(saveUserDetails({ email: data.email, ...response }));
    yield put(updateFormStatus(USER_DETAILS_COMPLETED));
  } catch (err) {
    yield put(showToast(err));
  } finally {
    yield put(disableLoaderAction());
  }
}

/**
 * @param {{leadSourceId: string, leadId:string, email:string, phone:string, merchantSiteUrl: string, cf_action_type: string, cf_cta_button: string, 'source-action': string }} freshSalesLeadPayload
 */
function* createFreshSalesLead(freshSalesLeadPayload = {}) {
  try {
    const { url, method } = CREATE_FRESHSALES_LEAD;
    const utmObject = getUTMObject();

    const sourceAction = freshSalesLeadPayload['source-action'];
    let { leadSourceId } = freshSalesLeadPayload;

    if (sourceAction) {
      const id = LEAD_SOURCE_ID_MAPPING[decodeURIComponent(sourceAction)];
      if (id) leadSourceId = id;
    }

    const options = {
      token: freshSalesLeadPayload.token,
      method,
      body: JSON.stringify({
        lead: {
          id: freshSalesLeadPayload.leadId,
          mobile_number: freshSalesLeadPayload.phone,
          first_name: 'N/A',
          last_name: 'N/A',
          email: freshSalesLeadPayload.email,
          company: {
            website: freshSalesLeadPayload.merchantSiteUrl,
          },
          lead_source_id: leadSourceId,
          custom_field: {
            cf_lead_type: 'Inbound',
            cf_monthly_transaction_volume_inr: 'N/A',
            lead_source_id: leadSourceId,
            cf_remarks: 'N/A',
            cf_lead_identifier: 'false',
            cf_are_you_an_existing_cashfree_merchant: 'no',
            cf_is_merchant_using_any_other_solution: 'yes',
            cf_action_type: freshSalesLeadPayload.cf_action_type,
            cf_cta_button: freshSalesLeadPayload.cf_cta_button,
            ...utmObject,
          },
        },
      }),
      track: true,
    };

    yield call(request, url, options);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.log(e);
  }
}

export function* updateTncInfo() {
  try {
    const [response] = yield call(request, GET_TNC_URL.url, {
      method: GET_TNC_URL.method,
      track: true,
      body: JSON.stringify({ tncNames: [TNC.SIGNUP] }),
    });

    if (response && !response.hasAccepted) {
      const { url, method } = UPDATE_TNC_URL;
      const options = {
        method,
        body: JSON.stringify({
          merchantTnCId: response.merchantTnCId,
          hasAccepted: true,
          hasViewed: true,
        }),
        track: true,
      };

      yield call(request, url, options);
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error(err);
  }
}

export function* updateMerchantDetails({ data }) {
  try {
    const { url, method } = UPDATE_MERCHANT_DETAILS_URL;
    const domain = getReferrerFromURL() || '';
    const body = JSON.parse(JSON.stringify(data));
    delete body.leadSourceId;

    // eslint-disable-next-line react-hooks/rules-of-hooks
    let sourceAction = useQuery().get('source-action');
    sourceAction = sourceAction ? decodeURIComponent(sourceAction) : 'Web';

    const channel = getItemFromLocalStorage('referrer') || domain;

    const options = {
      method,
      body: JSON.stringify({
        ...body,
        channel,
        refCode: getItemFromLocalStorage('refCode') || '',
        cf_action_type: getItemFromLocalStorage('action') || '',
        cf_cta_button: getItemFromLocalStorage('button-id') || '',
        source: SIGNUP_SOURCE,
        signUpMetadata: {
          cfLeadSource: sourceAction,
        },
      }),
      track: true,
    };

    const response = yield call(request, url, options);
    if (response) {
      yield* validateToken();
      yield* createFreshSalesLead({
        ...data,
        leadId: response.leadId,
      });
    }
    yield* updateTncInfo();

    yield put(saveMerchantDetails(response));
  } catch (err) {
    yield put(showToast(err));
    yield put(disableLoaderAction());
  }
}

export function* signupMerchant({ data }) {
  const { onSuccess, onError, ...rest } = data;
  try {
    const { url, method } = SIGNUP_MERCHANT_URL;

    const options = {
      method,
      body: JSON.stringify({
        ...rest,
      }),
      track: true,
      headers: {
        'X-Device-Source': getDeviceType(),
        'Content-Type': 'application/json',
      },
    };

    const response = yield call(request, url, options);
    if (onSuccess) onSuccess();
    if (response) {
      /**
       * in case when user had Step-1 complete already and Got redirect to Step-2 from Login page,
       * token won't be returned in this response
       * instead the Login API itself would return token in that particular case
       *
       * SO only set it to reducer and localStorage when token is returned
       */
      if (response.token) {
        yield put(
          saveUserDetails({ email: rest.email, token: response.token }),
        );
      }
      yield* validateToken();
    }
    yield* updateTncInfo();
    yield put(saveMerchantDetails(response));
    yield put(disableLoaderAction());
  } catch (err) {
    const error = err.errorMeta;
    if (onError) onError(error);
    yield put(showToast(err));
    yield put(disableLoaderAction());

    if (error.title === EMAIL_NOT_VERIFIED) {
      // wait for 2 sec before moving to step-1, so that toast message can appear
      yield delay(2000);
      // move back to step-1
      yield put(updateFormStatus(DEFAULT_STATUS));
    }
  }
}

export function* validateToken() {
  try {
    const requestURL = VALIDATE_TOKEN_URL;
    const data = yield call(request, requestURL);
    // update the redux state
    yield put(validateTokenSuccess(data));
  } catch (err) {
    yield put(disableLoaderAction());
    yield put(showToast(err));
  }
}

function* handleEmailLoginError(err, data) {
  const error = err.errorMeta || {};
  const {
    actions: { actions, onboardingActions },
    body,
    navigate,
    cbNoPwd,
    errorCb,
  } = data;

  // 2 Factor Authentication Enabled
  if (
    error.data &&
    (error.title === 'VERIFY_OTP' || error.title === 'VERIFY_G2FA')
  ) {
    onboardingActions.storeErrorLoginResponse(error);
    onboardingActions.toggleOTPModal(true);
  } else if (
    error.data &&
    error.data.errorCode === loginSignupErrorCode.WRONG_PASSWORD
  ) {
    actions.setFieldError('email', ' ');
    actions.setFieldError('password', err.message);
    errorCb();
  } else if (
    error.data &&
    error.data.errorCode === loginSignupErrorCode.NO_PASSWORD
  ) {
    actions.setFieldError('email', ' ');
    actions.setFieldError('password', err.message);
    cbNoPwd();
  } else if (
    error.data &&
    error.data.errorCode === loginSignupErrorCode.STEP2_INCOMPLETE &&
    error.data.token
  ) {
    navigate(`${basename}signup`);
    yield put(
      saveUserDetails({
        email: body.email,
        token: error.data.token,
      }),
    );
    // store step-1 data for later use
    yield put(
      setSignupFirstStepDataAction({
        ...body,
        userCreated: true, // For old users with Step-1 completed already
      }),
    );
    yield put(updateFormStatus(USER_DETAILS_COMPLETED));
  } else if (
    error.data &&
    error.data.errorCode === loginSignupErrorCode.RESET_PASSWORD
  ) {
    yield put(showToast(error));
    navigate(`${basename}forgot-password`);
  } else {
    errorCb();
    yield put(showToast(err));
  }
}

export function* authenticateEmailUser({ data }) {
  const {
    cb,
    actions: { actions },
    body,
  } = data;
  try {
    const { url, method } = AUTHENTICATE_EMAIL_USER_URL;
    const options = {
      method,
      body: JSON.stringify(body),
      track: true,
    };

    const response = yield call(request, url, options);

    setItemsInLocalStorage({ deviceId: body.deviceId });
    if (response) cb(response, response.token);
  } catch (err) {
    yield* handleEmailLoginError(err, data);
    yield put(disableLoaderAction());
  } finally {
    actions.setSubmitting(false);
  }
}

export function* handleAliasLoginError(err, data) {
  const error = err.errorMeta || {};
  const {
    actions: { onboardingActions, actions },
  } = data;

  // 2 Factor Authentication Enabled
  if (
    error.data &&
    (error.title === 'VERIFY_OTP' || error.title === 'VERIFY_G2FA')
  ) {
    onboardingActions.storeErrorLoginResponse(error);
    onboardingActions.toggleOTPModal(true);
  } else if (
    error.data &&
    error.data.errorCode === loginSignupErrorCode.WRONG_PASSWORD
  ) {
    actions.setFieldError('accountAlias', ' ');
    actions.setFieldError('username', ' ');
    actions.setFieldError('password', err.message);
  } else {
    yield put(showToast(err));
  }
}

export function* authenticateAlias({ data }) {
  const {
    cb,
    actions: { actions },
    body,
  } = data;
  try {
    const { url, method } = AUTHENTICATE_ALIAS_URL;
    const options = {
      method,
      body: JSON.stringify(body),
      track: true,
    };

    const response = yield call(request, url, options);

    setItemsInLocalStorage({ deviceId: body.deviceId });
    if (response) cb(response, response.token);
  } catch (err) {
    yield* handleAliasLoginError(err, data);
    yield put(disableLoaderAction());
  } finally {
    actions.setSubmitting(false);
  }
}

function* handleGoogleLoginError(err, data) {
  const error = err.errorMeta || {};
  const {
    body,
    navigate,
    errorCb,
    actions: { onboardingActions },
  } = data;
  if (
    error.data &&
    (error.title === 'VERIFY_OTP' || error.title === 'VERIFY_G2FA')
  ) {
    onboardingActions.storeErrorLoginResponse(error);
    onboardingActions.toggleOTPModal(true);
  } else if (
    error.data &&
    error.data.errorCode === loginSignupErrorCode.USER_NOT_FOUND
  ) {
    errorCb(error.data);
    setItemsInLocalStorage({ googleToken: body.googleToken });
  } else if (
    error.data &&
    error.data.errorCode === loginSignupErrorCode.STEP2_INCOMPLETE &&
    error.data.emailVerified === 'false'
  ) {
    errorCb(error.data);
    setItemsInLocalStorage({ googleToken: body.googleToken });
  } else if (
    error.data &&
    error.data.errorCode === loginSignupErrorCode.STEP2_INCOMPLETE &&
    error.data.token
  ) {
    // store step-1 data for later use
    yield put(
      setSignupFirstStepDataAction({
        googleToken: body.googleToken,
        deviceId: body.deviceId,
        userCreated: true, // For old users with Step-1 completed already
      }),
    );
    navigate(`${basename}signup`);
    yield put(
      saveUserDetails({
        email: body.email,
        token: error.data.token,
      }),
    );
    yield put(updateFormStatus(USER_DETAILS_COMPLETED));
  } else {
    yield put(showToast(err));
  }
}

export function* googleLogin({ data }) {
  const { successCb, body, formName } = data;
  try {
    const { url, method } = GOOGLE_LOGIN_URL;
    const options = {
      method,
      body: JSON.stringify(body),
    };

    const response = yield call(request, url, options);

    if (formName === SIGNUP && response) {
      yield put(saveUserDetails(response));
      yield put(updateFormStatus(USER_DETAILS_COMPLETED));
      yield put(disableLoaderAction());
    }
    if (response && successCb) {
      setItemsInLocalStorage({ googleToken: body.googleToken });
      successCb(response);
    }
  } catch (err) {
    yield* handleGoogleLoginError(err, data);
    yield put(disableLoaderAction());
  }
}

function* handleGoogleSignupError(err, data) {
  const error = err.errorMeta || {};
  const {
    body,
    errorCb,
    deviceId,
    actions: { onboardingActions },
  } = data;
  if (
    error.data &&
    (error.title === 'VERIFY_OTP' || error.title === 'VERIFY_G2FA')
  ) {
    onboardingActions.storeErrorLoginResponse(error);
    onboardingActions.toggleOTPModal(true);
  } else if (
    error.data &&
    error.data.errorCode === loginSignupErrorCode.SIGNUP_COMPLETE &&
    error.data.emailVerified === 'true'
  ) {
    body.deviceId = deviceId;
    yield put(googleLoginAction(data));
  } else if (
    error.data &&
    error.data.errorCode === loginSignupErrorCode.SIGNUP_COMPLETE &&
    error.data.emailVerified === 'false'
  ) {
    errorCb(error.data);
    setItemsInLocalStorage({ googleToken: body.googleToken });
  } else yield put(showToast(err));
}

export function* googleSignup({ data }) {
  const { body, navigate, formName } = data;
  try {
    const { url, method } = GOOGLE_SIGNUP_URL;
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const partnerRefCode = useQuery().get('refCode');
    const options = {
      method,
      body: JSON.stringify({
        ...body,
        refCode: partnerRefCode || '',
        metadata: getMetaData(),
      }),
    };

    const response = yield call(request, url, options);
    if (formName === LOGIN && navigate && response)
      navigate(`${basename}signup`);
    yield put(
      saveUserDetails({
        email: response.email,
        token: response.token,
      }),
    );
    yield put(updateFormStatus(USER_DETAILS_COMPLETED));
  } catch (err) {
    yield* handleGoogleSignupError(err, data);
  } finally {
    yield put(disableLoaderAction());
  }
}

export function* redeemVoucher({
  data: { cbSuccess, cbError, actions, ...body },
}) {
  try {
    const { url, method } = REDEEM_VOUCHER_URL;
    const options = {
      body: JSON.stringify(body),
      method,
    };
    const response = yield call(request, url, options);
    cbSuccess(response);
  } catch (err) {
    if (actions) actions.setSubmitting(false);
    cbError(err.errorMeta.message);
  }
}

export function* verifyLoginOTP({ data }) {
  const { body, cb, errorCb, resolve } = data;
  try {
    yield put(hideToast());
    const { url, method } = VERIFY_LOGIN_OTP_URL;
    const options = {
      body: JSON.stringify(body),
      method,
    };
    const response = yield call(request, url, options);
    resolve('SUCCESS');
    if (response) cb(response, response.token);
  } catch (err) {
    errorCb(err);
    resolve('ERROR');
    yield put(showToast(err));
    if (err.message === 'Invalid Code! 0 verification attempts remaining') {
      yield put(toggleOTPModalAction(false));
    }
    yield put(disableLoaderAction());
  }
}

export function* resendLoginOTP({ data: { data } }) {
  try {
    const { url, method } = RESEND_LOGIN_OTP_URL;
    const updatedURL = url.replace('$id$', data);
    const options = { method };
    // eslint-disable-next-line no-unused-vars
    const response = yield call(request, updatedURL, options);
    yield put(
      showToast({ type: 'SUCCESS', message: 'OTP Sent Successfully!' }),
    );
  } catch (err) {
    yield put(showToast(err));
    yield put(disableLoaderAction());
  }
}

export function* checkEmailExists({ data }) {
  const { navigate, onSuccess, onError, captchaResponse, ...rest } = data;
  try {
    const { url, method } = CHECK_EMAIL_EXISTS_URL;
    const options = {
      method,
      body: JSON.stringify({
        email: rest.email,
        googleToken: rest.googleToken, // this will be available only in case of Google Signup
        captchaResponse,
      }),
      track: true,
    };
    // store step-1 data for later use
    yield put(setSignupFirstStepDataAction(rest));

    const response = yield call(request, url, options);

    /**
     * Add the user email in GoogleTagManager's dataLayer, as it will capture the user email
     * on click of "Confirm" button in Phone OTP verification modal (Signup Step-2).
     *
     * Note that unavailability of email at the time of click on "Confirm" button, would result in
     * empty data being sent to marketing analytics.
     */
    window.dataLayer.push({
      email: response.email,
    });

    // move to step-2
    yield put(updateFormStatus(USER_DETAILS_COMPLETED));
    if (onSuccess) onSuccess(response);
  } catch (err) {
    yield put(showToast(err));
    const error = err.errorMeta;
    if (onError) onError(error);
  } finally {
    yield put(disableLoaderAction());
  }
}

/**
 *
 * @param {{data: {phone: string, countryCode: string, onSuccess?: Function}}} param0
 */
export function* sendSignupOTP({ data }) {
  const { onSuccess, onError, ...rest } = data;
  try {
    const { url, method } = SEND_SIGNUP_OTP_URL;
    const options = { method, body: JSON.stringify(rest) };
    const response = yield call(request, url, options);
    if (onSuccess) onSuccess(response);
    yield put(
      showToast({ type: 'SUCCESS', message: 'OTP Sent Successfully!' }),
    );
  } catch (err) {
    if (onError) onError(err.errorMeta);
    yield put(showToast(err));
    yield put(disableLoaderAction());
  }
}

export function* verifyImports({ data }) {
  try {
    const { captchaResponse, ...rest } = data;
    const { url, method } = VERIFY_IMPORTS_URL;

    const options = {
      method,
      body: JSON.stringify({
        ...rest,
      }),
      isImportMerchant: true,
      headers: {
        captchaToken: captchaResponse,
        'Content-Type': 'application/json',
      },
    };
    const response = yield call(request, url, options);

    if (response) {
      if (response.token) {
        yield put(
          saveUserDetails({ email: rest.email, token: response.token }),
        );
      }
    }
    yield put(saveMerchantDetails(response));
    yield put(disableLoaderAction());
  } catch (err) {
    yield put(showToast(err));
    yield put(disableLoaderAction());
  }
}

export function* verifyEmailOTP({
  data: { email, otp, captchaResponse, onSuccess, onError },
}) {
  try {
    const { url, method } = VERIFY_EMAIL_OTP_URL;
    const options = {
      method,
      body: JSON.stringify({ email, otp, captchaResponse }),
    };
    // eslint-disable-next-line no-unused-vars
    const response = yield call(request, url, options);

    // move to step-2
    yield put(updateFormStatus(USER_DETAILS_COMPLETED));
    if (onSuccess) onSuccess(response);
  } catch (err) {
    yield put(showToast(err));
    yield put(disableLoaderAction());
    const error = err.errorMeta;
    if (onError) onError(error);
  }
}

export function* resendEmailOTP({
  data: { email, captchaResponse, onSuccess, onError },
}) {
  try {
    const { url, method } = RESEND_EMAIL_OTP_URL;
    const options = {
      method,
      body: JSON.stringify({ email, captchaResponse }),
    };
    // eslint-disable-next-line no-unused-vars
    const response = yield call(request, url, options);
    yield put(
      showToast({ type: 'SUCCESS', message: 'OTP Sent Successfully!' }),
    );
    if (onSuccess) onSuccess(response);
  } catch (err) {
    yield put(showToast(err));
    yield put(disableLoaderAction());
    const error = err.errorMeta;
    if (onError) onError(error);
  }
}

export function* googleSignupSaga() {
  yield takeLatest(GOOGLE_SIGNUP, googleSignup);
}

export function* googleLoginSaga() {
  yield takeLatest(GOOGLE_LOGIN, googleLogin);
}

export function* authenticateAliasSaga() {
  yield takeLatest(AUTHENTICATE_ALIAS, authenticateAlias);
}

export function* authenticateEmailUserSaga() {
  yield takeLatest(AUTHENTICATE_EMAIL_USER, authenticateEmailUser);
}

export function* updateMerchantDetailsSaga() {
  yield takeLatest(UPDATE_MERCHANT_DETAILS, updateMerchantDetails);
}

export function* createMerchantSaga() {
  yield takeEvery(CREATE_MERCHANT, createMerchant);
}

export function* redeemVoucherSaga() {
  yield takeEvery(REDEEM_VOUCHER, redeemVoucher);
}

export function* verifyLoginOTPSaga() {
  yield takeEvery(VERIFY_LOGIN_OTP, verifyLoginOTP);
}
export function* resendLoginOTPSaga() {
  yield takeEvery(RESEND_LOGIN_OTP, resendLoginOTP);
}

export function* checkEmailExistsSaga() {
  yield takeLatest(CHECK_EMAIL_EXISTS, checkEmailExists);
}

export function* signupMerchantSaga() {
  yield takeLatest(SIGNUP_MERCHANT, signupMerchant);
}

export function* sendSignupOTPSaga() {
  yield takeLatest(SEND_SIGNUP_OTP, sendSignupOTP);
}

export function* verifyImportsSaga() {
  yield takeLatest(VERIFY_IMPORTS, verifyImports);
}

export function* verifyEmailOTPSaga() {
  yield takeLatest(VERIFY_EMAIL_OTP, verifyEmailOTP);
}

export function* resendEmailOTPSaga() {
  yield takeLatest(RESEND_EMAIL_OTP, resendEmailOTP);
}

// Individual exports for testing
export default function* onboardingContainerSaga() {
  // See example in containers/HomePage/saga.js
  const sagas = [
    createMerchantSaga,
    updateMerchantDetailsSaga,
    authenticateEmailUserSaga,
    authenticateAliasSaga,
    googleLoginSaga,
    googleSignupSaga,
    redeemVoucherSaga,
    verifyLoginOTPSaga,
    resendLoginOTPSaga,
    checkEmailExistsSaga,
    signupMerchantSaga,
    sendSignupOTPSaga,
    verifyImportsSaga,
    verifyEmailOTPSaga,
    resendEmailOTPSaga,
  ];

  yield all(
    sagas.map(saga =>
      // eslint-disable-next-line func-names
      spawn(function*() {
        while (true) {
          try {
            yield call(saga);
            break;
          } catch (e) {
            // eslint-disable-next-line no-console
            console.log(e);
          }
        }
      }),
    ),
  );
}
